import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, Warning, CurrencyCircleDollar, Phone, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import settlementsService from '../services/settlementsService';
import './MoMoWithdrawalModal.css';

function formatCurrency(amount) {
  return `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MoMoWithdrawalModal({ isOpen, onClose, balance, onSuccess, schoolPhoneNumber, schoolName }) {
  const [form, setForm] = useState({
    momo_number: '',
    recipient_name: '',
    amount: '',
    transfer_code: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({ 
        momo_number: schoolPhoneNumber || '', 
        recipient_name: schoolName || '', 
        amount: '',
        transfer_code: ''
      });
      setValidated(false);
      setError('');
      setVerifying(false);
    }
  }, [isOpen, schoolPhoneNumber, schoolName]);

  const handleAmountChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts[1];
    }
    setForm({ ...form, amount: value });
  };

  const validateForm = () => {
    setError('');

    if (!form.momo_number || form.momo_number.length !== 10) {
      setError('MoMo number must be 10 digits (e.g., 0244123456)');
      return false;
    }

    if (!form.momo_number.startsWith('0')) {
      setError('MoMo number must start with 0');
      return false;
    }

    if (!form.recipient_name.trim()) {
      setError('Recipient name is required');
      return false;
    }

    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      setError('Amount must be greater than zero');
      return false;
    }

    if (amount > balance) {
      setError(`Amount cannot exceed available balance (GHS ${balance})`);
      return false;
    }

    return true;
  };

  // Verify withdrawal status with Paystack
  const verifyWithdrawalNow = useCallback(async (transferCode) => {
    if (!transferCode) {
      console.warn('No transfer code provided for verification');
      return;
    }
    
    console.log('🔍 Starting withdrawal verification:', transferCode);
    setVerifying(true);
    
    try {
      console.log('📡 Calling verification endpoint for transfer:', transferCode);

      const response = await settlementsService.verifyTransfer(transferCode);
      
      console.log('✅ Verification response received:', response);
      
      if (response && response.success) {
        if (response.status === 'success') {
          // Paystack confirmed it's complete
          console.log('✓ Paystack confirmed COMPLETED');
          toast.success('Withdrawal verified and completed!');
          
          // Close immediately since we got confirmation
          setValidated(true);
          setTimeout(() => {
            onClose();
            setValidated(false);
            if (onSuccess) {
              onSuccess({ transfer_code: transferCode, status: 'completed' });
            }
          }, 1500);
        } else if (response.status === 'failed') {
          console.log('✗ Paystack marked as FAILED');
          toast.error('Withdrawal failed with Paystack');
          setError('Transfer failed. Please contact support.');
          setValidated(true);
        } else {
          // Still pending
          console.log('⏳ Transfer still PENDING - UI will auto-refresh');
          toast.info('Transfer initiated. Checking status...');
          setValidated(true);
          
          // Auto-close after 2 seconds since settlement view will refresh
          setTimeout(() => {
            onClose();
            setValidated(false);
            if (onSuccess) {
              onSuccess({ transfer_code: transferCode, status: 'pending' });
            }
          }, 2000);
        }
      } else {
        console.warn('❌ Verification returned success=false:', response);
        // Still show success state but let refresh handle updates
        setValidated(true);
        setTimeout(() => {
          onClose();
          setValidated(false);
          if (onSuccess) {
            onSuccess({ transfer_code: transferCode, status: 'pending' });
          }
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Verification endpoint error:', {
        message: err.message,
        status: err.status,
        error: err
      });
      
      // Even on error, show success state and let settlement refresh
      // The withdrawal was initiated, it may complete later
      console.log('⚠️ Verification failed, but withdrawal was initiated. UI will refresh via onSuccess.');
      setValidated(true);
      setTimeout(() => {
        onClose();
        setValidated(false);
        if (onSuccess) {
          onSuccess({ transfer_code: transferCode, status: 'pending' });
        }
      }, 2000);
    } finally {
      setVerifying(false);
    }
  }, [onClose, onSuccess]);

  const handleWithdraw = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await settlementsService.withdrawToMomo({
        momo_number: form.momo_number,
        recipient_name: form.recipient_name,
        amount: parseFloat(form.amount)
      });

      if (response.success) {
        const transferCode = response.transfer_code;
        setForm(prev => ({ ...prev, transfer_code: transferCode }));
        
        toast.success('Withdrawal initiated successfully!');
        console.log('Withdrawal Response:', response);
        
        // Immediately verify with Paystack (may already be complete)
        await verifyWithdrawalNow(transferCode);
      } else {
        setError(response.error || 'Withdrawal failed');
        toast.error(response.error || 'Withdrawal failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to initiate withdrawal');
      toast.error(err.message || 'Failed to initiate withdrawal');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay momo-withdrawal-modal" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>MoMo Withdrawal</h2>
          <button className="modal-close" onClick={onClose} disabled={validated}>
            <X size={20} />
          </button>
        </div>

        {validated ? (
          <div className="modal-body success-state">
            <div className="success-message">
              <div className="success-icon">
                <CheckCircle size={48} weight="fill" />
              </div>
              <h3>Withdrawal Initiated!</h3>
              <p className="success-details">
                Transfer Code: <strong>{form.transfer_code}</strong>
              </p>
              <p className="success-info">
                {formatCurrency(parseFloat(form.amount))} will be sent to {form.momo_number}
              </p>
              <p className="transfer-time">
                <Clock size={14} /> Transfers typically complete in 2-4 hours
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleWithdraw} className="modal-form">
            <div className="balance-display">
              <span className="balance-label">Available Balance:</span>
              <span className="balance-amount">{formatCurrency(balance)}</span>
            </div>

            {error && (
              <div className="error-alert">
                <Warning size={18} />
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label>School MoMo Number *</label>
              <input
                type="tel"
                value={form.momo_number}
                maxLength="10"
                required
                disabled={true}
                className="momo-input locked-input"
                title="This field is auto-filled with your school's registered phone number"
              />
              <small className="help-text"><CheckCircle size={12} /> Auto-filled from your school account (cannot be changed)</small>
            </div>

            <div className="form-group">
              <label>Recipient Name *</label>
              <input
                type="text"
                value={form.recipient_name}
                placeholder="Loading school name..."
                required
                disabled={true}
                className="recipient-input locked-input"
                title="This field is auto-filled with your school name"
              />
              <small className="help-text"><CheckCircle size={12} /> Auto-filled from your school account (cannot be changed)</small>
            </div>

            <div className="form-group">
              <label>Amount to Withdraw (GHS) *</label>
              <div className="amount-input-group">
                <span className="currency-symbol">₵</span>
                <input
                  type="text"
                  value={form.amount}
                  onChange={handleAmountChange}
                  placeholder={`0.00 (Min: 50, Max: ${balance})`}
                  required
                  disabled={loading}
                />
              </div>
              <div className="amount-suggestions">
                {balance > 50 && (
                  <>
                    <button
                      type="button"
                      className="suggestion-btn"
                      onClick={() => setForm({ ...form, amount: (balance * 0.25).toString() })}
                      disabled={loading}
                    >
                      25% ({formatCurrency(balance * 0.25)})
                    </button>
                    <button
                      type="button"
                      className="suggestion-btn"
                      onClick={() => setForm({ ...form, amount: (balance * 0.5).toString() })}
                      disabled={loading}
                    >
                      50% ({formatCurrency(balance * 0.5)})
                    </button>
                    <button
                      type="button"
                      className="suggestion-btn"
                      onClick={() => setForm({ ...form, amount: balance.toString() })}
                      disabled={loading}
                    >
                      All ({formatCurrency(balance)})
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="info-box">
              <div className="info-item">
                <span className="info-label">Processing Time:</span>
                <span className="info-value">2-4 hours</span>
              </div>
              <div className="info-item">
                <span className="info-label">Network Operator:</span>
                <span className="info-value">Auto-detected (MTN/Vodafone/AirtelTigo)</span>
              </div>
              <div className="info-item">
                <span className="info-label">Fee:</span>
                <span className="info-value">Included in amount</span>
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || parseFloat(form.amount) <= 0}
              >
                {loading ? (
                  <>
                    <span className="spinner-small"></span> Processing...
                  </>
                ) : (
                  <>
                    <Phone size={18} /> Withdraw {form.amount && formatCurrency(parseFloat(form.amount))}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default MoMoWithdrawalModal;
